/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import Tooltip from "@material-ui/core/Tooltip";
import withStyles from "@material-ui/core/styles/withStyles";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import React from "react";

import { PurchaserQuestionnaireQuestionHeader } from "./PurchaserQuestionnaireQuestionHeader";

import type { BinarySelectQuestionData } from "./purchaserQuestionnaireData";

type BinarySelectQuestionCardProps = {
  eventName?: string;
  question: BinarySelectQuestionData;
};

const ticketAnswerRateDescription =
  "1人が複数の券種を購入すると券種ごとに1件と数えるため、合計は全体の回答者数と一致しないことがあります。";

const TicketAnswerRateTooltip = withStyles({
  tooltip: {
    padding: 8,
    fontSize: 14,
  },
})(Tooltip);

export const BinarySelectQuestionCard: React.FC<
  BinarySelectQuestionCardProps
> = ({ eventName, question }) => {
  const ticketAnswerRateTitle = question.accentAnswer
    ? `券種別の「${question.accentAnswer}」回答率`
    : "券種別回答率";

  return (
    <article css={styles.card}>
      <PurchaserQuestionnaireQuestionHeader
        eventName={eventName}
        questionLabel={question.questionLabel}
        text={question.text}
      />

      <div css={styles.answerSegments} aria-hidden="true">
        {question.answers.map((answer, index) => (
          <div
            css={styles.answerSegment(answer.percentage, answer.isAccent)}
            key={`${answer.answer}-${index}`}
            title={`${answer.answer} ${formatPercentage(answer.percentage)}`}
          >
            {answer.percentage >= 12
              ? `${answer.answer} ${formatPercentage(answer.percentage)}`
              : null}
          </div>
        ))}
      </div>

      <div css={styles.answerSummary}>
        <ul css={styles.answerLegend}>
          {question.answers.map((answer, index) => (
            <li css={styles.answerLegendItem} key={`${answer.answer}-${index}`}>
              <span css={styles.legendMarker(answer.isAccent)} />
              <span css={styles.answerName}>{answer.answer}</span>
              <strong css={styles.answerCount}>
                {formatCount(answer.count)}人
              </strong>
            </li>
          ))}
        </ul>
        <span css={styles.totalAnswerCount}>
          回答者 {formatCount(question.totalAnswerCount)}人
        </span>
      </div>

      <section css={styles.ticketAnswerRates}>
        <div css={styles.ticketAnswerRatesHeading}>
          <h5 css={styles.ticketAnswerRatesTitle}>{ticketAnswerRateTitle}</h5>
          <TicketAnswerRateTooltip arrow title={ticketAnswerRateDescription}>
            <button
              aria-label="券種別回答率の集計方法"
              css={styles.ticketAnswerRateDescriptionButton}
              type="button"
            >
              <InfoOutlinedIcon fontSize="small" />
            </button>
          </TicketAnswerRateTooltip>
        </div>
        <ul css={styles.ticketAnswerRateList}>
          {question.ticketAnswerRates.map((ticketAnswerRate) => (
            <li
              css={styles.ticketAnswerRateItem}
              key={`${ticketAnswerRate.ticket.eventId}-${ticketAnswerRate.ticket.ticketId}`}
            >
              <div css={styles.ticketAnswerRateHeader}>
                <strong css={styles.ticketAnswerRatePercentage}>
                  {formatPercentage(ticketAnswerRate.percentage)}
                </strong>
                <span
                  css={styles.ticketName}
                  title={ticketAnswerRate.ticket.ticketName}
                >
                  {ticketAnswerRate.ticket.ticketName}
                </span>
                <span css={styles.ticketAnswerRateCount}>
                  {formatCount(ticketAnswerRate.accentAnswerCount)}件 /{" "}
                  {formatCount(ticketAnswerRate.respondentCount)}件
                </span>
              </div>
              <div
                css={styles.rateTrack}
                aria-label={`${ticketAnswerRate.ticket.ticketName} ${ticketAnswerRateTitle}`}
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuenow={ticketAnswerRate.percentage}
                role="progressbar"
              >
                <span css={styles.rateFill(ticketAnswerRate.percentage)} />
              </div>
            </li>
          ))}
        </ul>
      </section>
    </article>
  );
};

const formatCount = (count: number): string => count.toLocaleString("ja-JP");

const formatPercentage = (percentage: number): string =>
  `${percentage.toFixed(1)}%`;

const styles = {
  card: css`
    display: flex;
    flex-direction: column;
    min-width: 0;
    height: 100%;
    padding: clamp(16px, 2.5vw, 24px);
    background: #1b2734;
    border: 1px solid #2e3d4c;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgb(0 0 0 / 22%);
  `,
  answerSegments: css`
    display: flex;
    width: 100%;
    height: 46px;
    margin-top: 16px;
    font-variant-numeric: tabular-nums;
    border-radius: 8px;
    overflow: hidden;
  `,
  answerSegment: (percentage: number, isAccent: boolean) => css`
    display: flex;
    flex: none;
    align-items: center;
    justify-content: ${isAccent ? "flex-start" : "flex-end"};
    width: ${percentage}%;
    min-width: 0;
    padding: 0 13px;
    color: ${isAccent ? "#ffffff" : "#b9c3cd"};
    font-size: 13.5px;
    font-weight: 700;
    white-space: nowrap;
    background: ${isAccent ? "#00c2ae" : "#233140"};
    overflow: hidden;
  `,
  answerSummary: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
    margin-top: 12px;
  `,
  answerLegend: css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    padding: 0;
    margin: 0;
    list-style: none;
  `,
  answerLegendItem: css`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
    color: #b9c3cd;
    font-size: 13px;
  `,
  legendMarker: (isAccent: boolean) => css`
    flex: none;
    width: 10px;
    height: 10px;
    background: ${isAccent ? "#00c2ae" : "#233140"};
    border: 1px solid ${isAccent ? "#00c2ae" : "#2e3d4c"};
    border-radius: 3px;
  `,
  answerName: css`
    overflow-wrap: anywhere;
  `,
  answerCount: css`
    color: #ffffff;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  `,
  totalAnswerCount: css`
    margin-left: auto;
    color: #8b96a2;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  `,
  ticketAnswerRates: css`
    padding-top: 14px;
    margin-top: 16px;
    border-top: 1px solid #243342;
  `,
  ticketAnswerRatesHeading: css`
    display: flex;
    align-items: center;
    gap: 5px;
    margin: 0 0 13px;
  `,
  ticketAnswerRatesTitle: css`
    margin: 0;
    color: #b9c3cd;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.4px;
    line-height: 1.5;
    overflow-wrap: anywhere;
  `,
  ticketAnswerRateDescriptionButton: css`
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    color: #8b96a2;
    background: transparent;
    border: 0;
    border-radius: 50%;
    cursor: help;
    &:focus-visible {
      outline: 2px solid #00c2ae;
      outline-offset: 2px;
    }
  `,
  ticketAnswerRateList: css`
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 0;
    margin: 0;
    list-style: none;
  `,
  ticketAnswerRateItem: css`
    display: flex;
    flex-direction: column;
    gap: 7px;
  `,
  ticketAnswerRateHeader: css`
    display: flex;
    align-items: baseline;
    gap: 9px;
    min-width: 0;
  `,
  ticketAnswerRatePercentage: css`
    flex: none;
    width: 52px;
    color: #00c2ae;
    font-size: 13.5px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  `,
  ticketName: css`
    flex: 1;
    min-width: 0;
    color: #b9c3cd;
    font-size: 12.5px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  `,
  ticketAnswerRateCount: css`
    flex: none;
    color: #8b96a2;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  `,
  rateTrack: css`
    display: block;
    height: 9px;
    background: #243342;
    border-radius: 999px;
    overflow: hidden;
  `,
  rateFill: (percentage: number) => css`
    display: block;
    width: ${percentage}%;
    height: 100%;
    background: #00c2ae;
    border-radius: 999px;
  `,
};
